import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Modal, Spin } from "antd";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  DashboardMember,
  DashboardNotification,
  MemberStage,
  DashboardSummary,
  dashboardSummary as mockDashboardSummary,
  stageDistribution as mockStageDistribution,
} from "../../../../mock/teamDashboard";
import {
  getTeamDetailOverview,
  getTeamDetailMetrics,
  getTeamDetailCharts,
  getTeamDetailTrackingTable
} from "../funtion";
import {
  MemberMetrics,
  TeamDetailOverviewApi,
  TeamDetailChartDataApi,
  TeamDetailTrackingTable,
  TreemapSeries,
  TmItem,
  TmRect
} from "../type";
import { CheckSquareOffset, CalendarBlank } from '@phosphor-icons/react';
import "../style/dashboard.scss";

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

// How many notifications to show at the top of the page.
const TOP_NOTIF_LIMIT = 6;



function hexToRgb(hex: string) {
  const h = hex.replace("#", "");
  return { r: parseInt(h.substr(0, 2), 16), g: parseInt(h.substr(2, 2), 16), b: parseInt(h.substr(4, 2), 16) };
}
function mixColor(c1: string, c2: string, t: number) {
  const a = hexToRgb(c1);
  const b = hexToRgb(c2);
  const r = Math.round(a.r + (b.r - a.r) * t);
  const g = Math.round(a.g + (b.g - a.g) * t);
  const bch = Math.round(a.b + (b.b - a.b) * t);
  return `rgb(${r},${g},${bch})`;
}
function pnlToColor(pnl: number) {
  const t = Math.max(-1, Math.min(1, pnl / 8));
  if (t >= 0) return mixColor("#E3F2E6", "#1B7A37", t);
  return mixColor("#FBE4E4", "#C0392B", -t);
}
function textColorFor(rgbStr: string) {
  const m = (rgbStr.match(/\d+/g) || []).map(Number);
  const lum = (0.299 * m[0] + 0.587 * m[1] + 0.114 * m[2]) / 255;
  return lum > 0.62 ? "#1A1814" : "#FFFFFF";
}


function squarify(items: TmItem[], x: number, y: number, w: number, h: number): TmRect[] {
  if (!items.length) return [];
  if (items.length === 1) return [{ ...items[0], rx: x, ry: y, rw: w, rh: h }];

  const total = items.reduce((s, d) => s + d.value, 0);
  const shortSide = Math.min(w, h);

  let bestSplit = 1;
  let bestWorst = Infinity;
  let runningSum = 0;
  for (let i = 0; i < items.length; i++) {
    runningSum += items[i].value;
    const rowArea = (runningSum / total) * w * h;
    const rowSide = rowArea / shortSide;
    let worst = 0;
    for (let j = 0; j <= i; j++) {
      const itemArea = (items[j].value / total) * w * h;
      const otherSide = itemArea / rowSide;
      const ratio = Math.max(rowSide / otherSide, otherSide / rowSide);
      worst = Math.max(worst, ratio);
    }
    if (worst <= bestWorst) {
      bestWorst = worst;
      bestSplit = i + 1;
    } else break;
  }

  const row = items.slice(0, bestSplit);
  const rest = items.slice(bestSplit);
  const rowSum = row.reduce((s, d) => s + d.value, 0);
  const rowArea = (rowSum / total) * w * h;

  let result: TmRect[] = [];
  if (w >= h) {
    const colW = rowArea / h;
    let cy = y;
    row.forEach((item) => {
      const itemH = ((item.value / total) * w * h) / colW;
      result.push({ ...item, rx: x, ry: cy, rw: colW, rh: itemH });
      cy += itemH;
    });
    result = result.concat(squarify(rest, x + colW, y, w - colW, h));
  } else {
    const rowH = rowArea / w;
    let cx = x;
    row.forEach((item) => {
      const itemW = ((item.value / total) * w * h) / rowH;
      result.push({ ...item, rx: cx, ry: y, rw: itemW, rh: rowH });
      cx += itemW;
    });
    result = result.concat(squarify(rest, x, y + rowH, w, h - rowH));
  }
  return result;
}

// Time formatting
function timeAgo(iso: string, t: (k: string, o?: any) => string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t("JustNow");
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

const NOTIF_ICON: Record<DashboardNotification["level"], string> = {
  urgent: "🚨",
  warning: "⚠️",
  success: "✅",
  info: "ℹ️",
};

// Member table helpers
const STAGE_MAP: Record<MemberStage, { c: string; bg: string; label: string }> = {
  funded: { c: "#2D7A3A", bg: "#E8F5EB", label: "Funded" },
  challenge: { c: "#1A5FA8", bg: "#E8F1FC", label: "Challenge" },
  verification: { c: "#5B3FA8", bg: "#EEE8FC", label: "Verification" },
  new: { c: "#9B9589", bg: "#F0EDE6", label: "Mới" },
};

type TableFilter = "all" | "funded" | "challenge" | "verification" | "danger";

// --- CÁC HÀM HELPER ĐỊNH DẠNG CHO TOOLTIP ---
const getInitials = (name: string) => {
  if (!name) return "";
  const parts = name.split(" ");
  return parts.length > 1
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : parts[0][0].toUpperCase();
};

const fmtBalance = (val?: number | null) => {
  if (val === undefined || val === null || isNaN(val)) return "0 USD";
  return `${val.toLocaleString()} USD`;
};

const fmtPnl = (pnl: number) => {
  return `${Math.abs(pnl)}%`;
};

const pnlArrow = (pnl: number) => {
  if (pnl > 0) return "▲ ";
  if (pnl < 0) return "▼ ";
  return "";
};

// Đổi vị trí tooltip nếu ô nằm quá sát rìa trái/phải (ở đây tạm để center, bạn có thể custom thêm)
const getTooltipPositionClass = (index: number) => {
  return "left-1/2 -translate-x-1/2";
};

const TOOLTIP_WIDTH = 220;
const TOOLTIP_HEIGHT = 190; // Chiều cao ước tính của hộp tooltip + mũi tên

const calculateTooltipStyle = (r: any, layoutW: number, layoutH: number) => {
  // 1. Tìm điểm chính giữa của ô thỏi theo trục X
  let left = r.rx + r.rw / 2 - TOOLTIP_WIDTH / 2;

  // Giới hạn biên Trái / Phải để không lọt ra ngoài khung chart
  if (left < 5) left = 5;
  if (left + TOOLTIP_WIDTH > layoutW - 5) left = layoutW - TOOLTIP_WIDTH - 5;

  // 2. Mặc định hiển thị phía trên ô (Cách ô một khoảng 10px)
  let top = r.ry - TOOLTIP_HEIGHT;
  let isBottomMode = false;

  // Nếu ô nằm quá sát mép TRÊN, không đủ chỗ hiện hộp tooltip -> Đẩy nó xuống HIỂN THỊ PHÍA DƯỚI ô
  if (top < 5) {
    top = r.ry + r.rh + 10;
    isBottomMode = true;
  }

  return {
    style: {
      position: "absolute" as const,
      left: `${left}px`,
      top: `${top}px`,
      width: `${TOOLTIP_WIDTH}px`,
      zIndex: 999,
    },
    isBottomMode,
    // Tính vị trí mũi tên tam giác lệch theo ô đang hover
    arrowLeft: r.rx + r.rw / 2 - left
  };
};

interface TreemapProps {
  data: TreemapSeries[]; // Thay thế bằng type chuẩn của bạn: TreemapSeries[]
}

const Treemap: React.FC<TreemapProps> = ({ data }) => {
  const wrapRef = useRef<HTMLDivElement>(null);

  const [layout, setLayout] = useState<{ rects: any[]; w: number; h: number }>({
    rects: [],
    w: 400,
    h: 400,
  });

  // Lưu thông tin ô đang hover và tọa độ chuột hiện tại (tương đối so với container)
  const [activeRect, setActiveRect] = useState<any | null>(null);
  const [mousePos, setMousePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  useEffect(() => {
    const render = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      if (w === 0 || h === 0) return;

      const tmData = data
        .map((m) => {
          const fullName = `${m.first_name} ${m.last_name}`.trim();
          return {
            name: fullName,
            accountNumber: m.account_number,
            value: Math.max(0, m.account_balance),
            account_balance: m.account_balance,
            account_size: m.account_size,
            sizeK: Number((m.account_balance / 1000).toFixed(2)),
            pnl: m.pnl_percent,
            dd: m.is_danger ? 7 : 0,
            drawdown: m.drawdown || 0,
            fullName: fullName,
          };
        })
        .sort((a, b) => b.value - a.value);

      // @ts-ignore
      const calculatedRects = squarify(tmData, 0, 0, w, h) || [];
      setLayout({ rects: calculatedRects, w, h });
    };

    render();
    let timer: ReturnType<typeof setTimeout>;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(render, 150);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      clearTimeout(timer);
    };
  }, [data]);

  // 🌟 Hàm xử lý khi con trỏ di chuyển bên trong ô thỏi
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, rect: any) => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    // Lấy vị trí biên của container đồ thị
    const containerRect = wrap.getBoundingClientRect();

    // Tính toán tọa độ chuột tương đối bên trong đồ thị (Chính xác 100%)
    const mouseX = e.clientX - containerRect.left;
    const mouseY = e.clientY - containerRect.top;

    setActiveRect(rect);
    setMousePos({ x: mouseX, y: mouseY });
  };

  // 🌟 Hàm tính toán vị trí Tooltip dựa theo tọa độ chuột hiện tại
  const getTooltipStyle = () => {
    if (!activeRect) return { style: {}, isBottomMode: false, arrowLeft: 0 };

    // Giữ tâm của tooltip trùng hoặc lệch nhẹ so với con trỏ (ví dụ: căn giữa theo trục X của con trỏ)
    let left = mousePos.x - TOOLTIP_WIDTH / 2;

    // Khống chế biên Trái / Phải không cho tràn ra ngoài khung chart
    if (left < 5) left = 5;
    if (left + TOOLTIP_WIDTH > layout.w - 5) left = layout.w - TOOLTIP_WIDTH - 5;

    // Mặc định: Để Tooltip cách con trỏ chuột lên phía trên 15px
    let top = mousePos.y - TOOLTIP_HEIGHT - 25;
    let isBottomMode = false;

    // Nếu con trỏ chuột nằm quá sát mép TRÊN của chart -> Lật Tooltip xuống dưới con trỏ chuột 20px
    if (top < 5) {
      top = mousePos.y + 20;
      isBottomMode = true;
    }

    return {
      style: {
        position: "absolute" as const,
        left: `${left}px`,
        top: `${top}px`,
        width: `${TOOLTIP_WIDTH}px`,
        zIndex: 999,
      },
      isBottomMode,
      // Mũi tên tam giác sẽ chạy theo đúng tọa độ X của chuột
      arrowLeft: mousePos.x - left
    };
  };

  const tooltipConfig = getTooltipStyle();

  return (
    <div
      className="tdv2-treemap"
      ref={wrapRef}
      style={{
        position: "relative",
        width: "100%",
        height: "400px",
        minHeight: "350px",
        background: "#141b24",
        overflow: "hidden", // 🌟 Ẩn toàn bộ phần lòi ra ngoài của chart thoải mái
      }}
    >
      {/* LAYER BIỂU ĐỒ TRỰC QUAN */}
      {layout.rects.map((r, i) => {
        const bg = pnlToColor(r.pnl);
        const txt = textColorFor(bg);

        const safeWidth = r.rx + r.rw > layout.w ? layout.w - r.rx : r.rw;
        const safeHeight = r.ry + r.rh > layout.h ? layout.h - r.ry : r.rh;
        const small = safeWidth < 90 || safeHeight < 65;

        return (
          <div
            key={i}
            className={`tdv2-tm-cell${small ? " small" : ""}${r.dd >= 7 ? " danger" : ""}`}
            // 🌟 Thay đổi Event ở đây: sử dụng MouseMove và MouseLeave
            onMouseMove={(e) => handleMouseMove(e, r)}
            onMouseLeave={() => setActiveRect(null)}
            style={{
              position: "absolute",
              left: r.rx,
              top: r.ry,
              width: safeWidth,
              height: safeHeight,
              background: bg,
              color: txt,
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              padding: "6px",
              boxSizing: "border-box",
              border: "1px solid rgba(0,0,0,0.15)",
              zIndex: activeRect === r ? 10 : 1,
            }}
          >
            <div>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "12px", fontWeight: "bold" }}>
                {r.name}
              </div>
              {!small && <div style={{ fontSize: "11px", opacity: 0.8, marginTop: "1px" }}>#{r.accountNumber}</div>}
            </div>
            <div>
              <div style={{ fontSize: "14px", fontWeight: "bold" }}>{r.pnl >= 0 ? "+" : ""}{r.pnl}%</div>
              {!small && <div style={{ fontSize: "11px", opacity: 0.8 }}>{r.sizeK}k USD</div>}
            </div>
          </div>
        );
      })}

      {/* LAYER TOOLTIP DI CHUYỂN THEO CON TRỎ CHUỘT */}
      {activeRect && (
        <div
          style={tooltipConfig.style}
          className="pointer-events-none transition-all duration-75 ease-out"
        >
          {/* Mũi tên hướng lên nếu Tooltip nằm dưới chuột */}
          {tooltipConfig.isBottomMode && (
            <svg
              className="block w-3 h-1.5 text-white fill-current drop-shadow-[0_-1px_0_rgba(0,0,0,0.1)] mb-[-1px]"
              style={{ marginLeft: `${tooltipConfig.arrowLeft - 6}px` }}
              viewBox="0 0 12 7"
            >
              <polygon points="6,0 12,7 0,7" />
            </svg>
          )}

          {/* Nội dung Tooltip */}
          <div className="bg-white border border-black/10 rounded-xl p-4 shadow-[0_8px_24px_rgba(0,0,0,0.12)] text-left normal-case font-normal text-slate-800">
            <div className="flex items-center gap-2.5 pb-2.5 border-b border-[#eef0f3] mb-2.5">
              <div className={`w-8 h-8 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${activeRect.pnl >= 0 ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>
                {getInitials(activeRect.name)}
              </div>
              <div className="min-w-0 w-full">
                <div className="text-sm font-semibold text-[#0b1c2e] leading-tight truncate max-w-[140px]">{activeRect.name}</div>
                <div className="text-xs text-[#94a3b8] mt-0.5">#{activeRect.accountNumber}</div>
              </div>
            </div>

            {/* Init Balance */}
            <div className="flex justify-between items-center mb-1.5 text-xs">
              <span className="text-[#64748b]">Số vốn ban đầu</span>
              <span className="text-sm font-semibold text-[#0b1c2e]">{fmtBalance(activeRect.account_size)}</span>
            </div>

            {/* Balance */}
            <div className="flex justify-between items-center mb-1.5 text-xs">
              <span className="text-[#64748b]">Số dư</span>
              <span className="text-sm font-semibold text-[#0b1c2e]">{fmtBalance(activeRect.account_balance ?? activeRect.value)}</span>
            </div>

            <div className="flex justify-between items-center mb-1.5 text-xs">
              <span className="text-[#64748b]">P&amp;L tháng</span>
              <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-semibold ${activeRect.pnl >= 0 ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"}`}>
                {pnlArrow(activeRect.pnl)}{fmtPnl(activeRect.pnl)}
              </span>
            </div>

            <div className="flex justify-between items-center text-xs">
              <span className="text-[#64748b]">Drawdown</span>
              <span className="text-sm font-semibold" style={{ color: activeRect.drawdown > 3 ? "#a32d2d" : "#334155" }}>
                {(activeRect.drawdown || 0).toFixed(2)}%
              </span>
            </div>
          </div>

          {/* Mũi tên hướng xuống nếu Tooltip nằm trên chuột */}
          {!tooltipConfig.isBottomMode && (
            <svg
              className="block w-3 h-1.5 text-white fill-current drop-shadow-[0_1px_0_rgba(0,0,0,0.1)] mt-[-1px]"
              style={{ marginLeft: `${tooltipConfig.arrowLeft - 6}px` }}
              viewBox="0 0 12 7"
            >
              <polygon points="0,0 12,0 6,7" />
            </svg>
          )}
        </div>
      )}
    </div>
  );
};

const NotifItem: React.FC<{ n: DashboardNotification }> = ({ n }) => {
  const { t } = useTranslation();
  return (
    <div className={`tdv2-notif ${n.level}`}>
      <span className="ic">{NOTIF_ICON[n.level]}</span>
      <div className="content">
        <div className="row1">
          <span className="member">{n.member}</span>
          <span className="title">— {n.title}</span>
          {!n.read && <span className="badge">{t("New")}</span>}
          <span className="time">{timeAgo(n.createdAt, t)}</span>
        </div>
        <div className="body">{n.body}</div>
      </div>
    </div>
  );
};

const TeamDashboardTabV2: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const [filter, setFilter] = useState<TableFilter>("all");
  const [period, setPeriod] = useState<"today" | "week" | "month" | "all" | "custom">("custom");
  const [statusOpen, setStatusOpen] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);

  const getDefaultDateRange = () => {
    const to = new Date().toISOString().split("T")[0];
    const from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    return { from, to };
  };

  const [dateRange, setDateRange] = useState<{ from: string; to: string }>(getDefaultDateRange);
  const [notifOpen, setNotifOpen] = useState(false);
  const [modalTab, setModalTab] = useState<"unread" | "read">("unread");

  const [overview, setOverview] = useState<TeamDetailOverviewApi["data"] | null>(null);
  const [metrics, setMetrics] = useState<MemberMetrics[]>([]);
  const [charts, setCharts] = useState<TeamDetailChartDataApi["data"] | null>(null);
  const [trackingTable, setTrackingTable] = useState<TeamDetailTrackingTable[]>([]);
  const [notifications, setNotifications] = useState<DashboardNotification[]>([]);

  const [dataStartDate, setDataStartDate] = useState<string>("");
  const [dataEndDate, setDataEndDate] = useState<string>("");

  const [tableStartDate, setTableStartDate] = useState<string>(() => {
    return new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
  });
  const [tableEndDate, setTableEndDate] = useState<string>(() => {
    return new Date().toISOString().split("T")[0];
  });

  const [loading, setLoading] = useState(true);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClose = () => {
      setStatusOpen(false);
      setRangeOpen(false);
    };
    window.addEventListener("click", handleClose);
    return () => window.removeEventListener("click", handleClose);
  }, []);

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [ov, me, ch, tr] = await Promise.all([
          getTeamDetailOverview(Number(id)),
          getTeamDetailMetrics(Number(id)),
          getTeamDetailCharts(Number(id)),
          getTeamDetailTrackingTable(Number(id), tableStartDate, tableEndDate),
        ]);
        if (ov.success) setOverview(ov.data || null);
        if (me.success) setMetrics(me.data || []);
        if (ch.success) setCharts(ch.data);
        if (tr.success) setTrackingTable(tr.data || []);
      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, tableStartDate, tableEndDate]);

  const dashboardSummary = useMemo<DashboardSummary>(() => {
    if (!overview) return mockDashboardSummary;
    return {
      totalMembers: overview.total_members,
      growing: overview.growing_accounts,
      danger: overview.at_risk_accounts,
      funded: overview.funded_accounts,
      passable: overview.passing_potential,
      monthlyLosses: overview.losing_trades,
      slViolations: overview.sl_violations,
      missingJournal: overview.missing_journal,
    };
  }, [overview]);

  const dashboardMembers = useMemo<DashboardMember[]>(() => {
    if (!trackingTable.length) return [];
    return trackingTable.map((m) => {
      const stageMap: Record<string, MemberStage> = {
        funded: "funded",
        challenge: "challenge",
        verification: "verification",
        new: "new",
        newbie: "new",
      };
      return {
        id: m.account_number.slice(-3).toUpperCase(),
        name: `${m.first_name} ${m.last_name}`,
        avatar_url: m.avatar_url,
        shortName: m.last_name,
        size: m.account_size.toFixed(0),
        sizeK: m.account_size / 1000,
        stage: stageMap[m.phase?.toLowerCase()] || "new",
        account_number: m.account_number,
        pnl: m.pnl_week,
        dd: m.drawdown,
        road: m.roadmap_pnl || 0,
        roadTarget: 10,
        sl: m.no_sl_count,
        journal: m.missing_journal,
        avColor: "#1A5FA8",
        avBg: "#E8F1FC",
        passScore: m.roadmap_pnl || 0,
        losses: 0,
        totalTrades: 0,
        healthStatus: m.health_status,
        alerts: m.alerts,
        platform: m.platform || '',
      };
    });
  }, [trackingTable]);

  const stageDistribution = useMemo(() => {
    if (!charts?.distribution?.phases) return mockStageDistribution;
    const p = charts.distribution.phases;
    return [
      { label: "Funded", value: p.funded, color: "#2D7A3A" },
      { label: "Challenge", value: p.challenge, color: "#1A5FA8" },
      { label: "Verification", value: p.verification, color: "#5B3FA8" },
      { label: "Mới", value: p.newbie, color: "#9B9589" },
    ];
  }, [charts]);

  // Top notifications: newest first, urgent surfaces even if older.
  const topNotifications = useMemo(() => {
    const sorted = notifications.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    const urgent = sorted.filter((n) => n.level === "urgent");
    const rest = sorted.filter((n) => n.level !== "urgent");
    const merged = [...urgent, ...rest.filter((n) => !urgent.includes(n))];
    return merged.slice(0, TOP_NOTIF_LIMIT);
  }, []);

  const modalNotifications = useMemo(() => {
    const sorted = notifications.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return sorted.filter((n) => (modalTab === "unread" ? !n.read : n.read));
  }, [modalTab]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, []);

  const filteredMembers = useMemo(() => {
    return dashboardMembers.filter((m) => {
      // 1. Filter by status
      if (filter !== "all") {
        if (filter === "funded" && m.stage !== "funded") return false;
        if (filter === "challenge" && m.stage !== "challenge") return false;
        if (filter === "verification" && m.stage !== "verification") return false;
        if (filter === "danger" && !(m.dd >= 7 || m.sl >= 5)) return false;
      }

      // 2. Filter by period (mocking logic since we don't have per-day data in this view)
      // In a real app, you'd check trade dates. For now we just return true.

      return true;
    });
  }, [filter, dashboardMembers, period, dateRange]);

  // Chart data
  const stageChartData = useMemo(() => ({
    labels: stageDistribution.map((s) => s.label),
    datasets: [{ data: stageDistribution.map((s) => s.value), backgroundColor: stageDistribution.map((s) => s.color), borderWidth: 0, hoverOffset: 4 }],
  }), [stageDistribution]);

  const pnlGroups = useMemo(() => {
    if (charts?.distribution?.trades) {
      return {
        Lãi: charts.distribution.trades.win,
        Lỗ: charts.distribution.trades.loss,
        Hòa: charts.distribution.trades.breakeven,
      };
    }
    return { Lãi: 0, Lỗ: 0, Hòa: 0 };
  }, [charts]);

  const passChanceDataFromChart = useMemo(() => {
    if (!charts?.member_charts?.pass_probability) return [];
    const { account_numbers, first_name, last_name, data } = charts.member_charts.pass_probability;
    if (!account_numbers) return [];
    return account_numbers.map((account, i) => ({
      shortName: account,
      passScore: data?.[i] || 0,
      label: `${first_name?.[i] || ""} ${last_name?.[i] || ""}`
    }));
  }, [charts]);

  const passChanceSorted = useMemo(
    () => [...passChanceDataFromChart].sort((a, b) => b.passScore - a.passScore),
    [passChanceDataFromChart]
  );
  const passScores = passChanceSorted.map((p) => p.passScore);
  const passNames = passChanceSorted.map((p) => p.shortName);

  const losingTradesData = useMemo(() => {
    if (!charts?.member_charts?.losing_trades) return { labels: [], datasets: [] };
    const { account_numbers, first_name, last_name, data } = charts.member_charts.losing_trades;
    if (!account_numbers || !data) return { labels: [], datasets: [] };

    // 1. Gộp dữ liệu hợp lệ (value > 0) vào một mảng các object để dễ quản lý và sắp xếp
    const combinedData = data
      .map((value, index) => ({
        value,
        label: account_numbers[index],
        name: `${first_name?.[index] || ""} ${last_name?.[index] || ""}`.trim(),
      }))
      .filter((item) => item.value > 0); // Lọc bỏ các giá trị <= 0

    // 2. Sắp xếp giảm dần theo giá trị (lớn nhất đứng trước)
    combinedData.sort((a, b) => b.value - a.value);

    // 3. Tách ngược lại thành các mảng riêng biệt cho chart dữ liệu đồng nhất
    const sortedLabels = combinedData.map((item) => item.label);
    const sortedData = combinedData.map((item) => item.value);
    const sortedNames = combinedData.map((item) => item.name);

    return {
      labels: sortedLabels,
      datasets: [
        {
          data: sortedData,
          names: sortedNames,
          backgroundColor: sortedData.map((v) => (v >= 9 ? "#E24B4A" : v >= 6 ? "#EF9F27" : "#B4B2A9")),
          borderRadius: 4,
          borderSkipped: false,
        } as any,
      ],
    };
  }, [charts]);

  const violationsData = useMemo(() => {
    if (!charts?.member_charts?.violations) return { labels: [], datasets: [] };
    const { account_numbers, no_sl, missing_journal } = charts.member_charts.violations;
    if (!account_numbers) return { labels: [], datasets: [] };

    const safeNoSl = no_sl || [];
    const safeMissingJournal = missing_journal || [];

    // 1. Gộp dữ liệu của từng tài khoản lại và lọc các tài khoản có vi phạm (> 0)
    const combinedData = account_numbers
      .map((acc, index) => {
        const journalVal = safeMissingJournal[index] || 0;
        const noSlVal = safeNoSl[index] || 0;

        // Tìm tên thành viên tương ứng
        const m = dashboardMembers.find((member) => member.account_number === acc || member.id === acc);
        const name = m ? m.name : acc;

        return {
          label: acc,
          noSl: noSlVal,
          missingJournal: journalVal,
          name: name,
        };
      })
      // Tài khoản được giữ lại nếu ít nhất có 1 loại vi phạm > 0
      .filter((item) => item.noSl > 0 || item.missingJournal > 0);

    // 2. Sắp xếp: Ưu tiên missingJournal giảm dần, nếu bằng nhau thì xét noSl giảm dần
    combinedData.sort((a, b) => {
      if (b.missingJournal !== a.missingJournal) {
        return b.missingJournal - a.missingJournal; // Thằng nào thiếu journal nhiều hơn lên trước
      }
      return b.noSl - a.noSl; // Nếu thiếu journal bằng nhau, thằng nào no_sl nhiều hơn lên trước
    });

    // 3. Tách ngược lại thành các mảng đơn để đưa vào Chart
    const sortedLabels = combinedData.map((item) => item.label);
    const sortedNoSl = combinedData.map((item) => item.noSl);
    const sortedMissingJournal = combinedData.map((item) => item.missingJournal);
    const sortedNames = combinedData.map((item) => item.name);

    return {
      labels: sortedLabels,
      datasets: [
        {
          label: t("NoStopLoss"),
          data: sortedNoSl,
          names: sortedNames,
          backgroundColor: "#E24B4A",
          borderRadius: 4,
          borderSkipped: false,
        } as any,
        {
          label: t("MissingJournal"),
          data: sortedMissingJournal,
          names: sortedNames,
          backgroundColor: "#EF9F27",
          borderRadius: 4,
          borderSkipped: false,
        } as any,
      ],
    };
  }, [charts, t, dashboardMembers]);

  const baseBarOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (context: any) => {
            const ds = context[0].dataset;
            return ds.names ? ds.names[context[0].dataIndex] : context[0].label;
          },
        },
      },
    },
  };

  useEffect(() => {
    console.log("Dashboard data updated:", { overview, metrics, charts, trackingTable });
  }, [overview, metrics, charts, trackingTable]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="team-dash-v2">

      <div className="tdv2-main-grid">

        {/* LEFT COLUMN */}
        <div className="tdv2-main-left">
          <div className="tdv2-section-hd">{t("TeamOverview")}</div>
          <div className="tdv2-metrics">
            <div className="tdv2-metric" data-tooltip={t("Teams_dashboard.Tooltip.totalMembers") || "Tổng số thành viên trong hệ thống"}>
              <div className="lbl">{t("TotalMembers")}</div>
              <div className="val gr">{dashboardSummary.totalMembers}</div>
              <div className="sub-val">{t("Active")}</div>
            </div>

            <div className="tdv2-metric" data-tooltip={t("Teams_dashboard.Tooltip.growing") || "Tài khoản đang tăng trưởng ổn định"}>
              <div className="lbl">{t("Growing")}</div>
              <div className="val g">{dashboardSummary.growing}</div>
              <div className="sub-val">{t("ProfitableAccounts")}</div>
            </div>

            <div className="tdv2-metric" data-tooltip={t("Teams_dashboard.Tooltip.danger") || "Tài khoản vi phạm nghiêm trọng, cần can thiệp gấp"}>
              <div className="lbl">{t("Danger")}</div>
              <div className="val r">{dashboardSummary.danger}</div>
              <div className="sub-val">{t("NeedIntervention")}</div>
            </div>

            <div className="tdv2-metric" data-tooltip={t("Teams_dashboard.Tooltip.funded") || "Tài khoản đã được cấp vốn và đang nhận chia sẻ lợi nhuận"}>
              <div className="lbl">Funded</div>
              <div className="val b">{dashboardSummary.funded}</div>
              <div className="sub-val">{t("ReceivingPayout")}</div>
            </div>

            <div className="tdv2-metric" data-tooltip={t("Teams_dashboard.Tooltip.passable") || "Tỷ lệ tài khoản đi đúng lộ trình và có khả năng qua quỹ cao"}>
              <div className="lbl">{t("PassChance")}</div>
              <div className="val g">{dashboardSummary.passable}</div>
              <div className="sub-val">{t("GoodRoadmap")}</div>
            </div>

            <div className="tdv2-metric" data-tooltip={t("Teams_dashboard.Tooltip.monthlyLosses") || "Tổng số lệnh thua lỗ ghi nhận trong tháng này"}>
              <div className="lbl">{t("LossesPerMonth")}</div>
              <div className="val a">{dashboardSummary.monthlyLosses}</div>
              <div className="sub-val">{t("TeamTotal")}</div>
            </div>

            <div className="tdv2-metric" data-tooltip={t("Teams_dashboard.Tooltip.slViolations") || "Số lần tài khoản giao dịch không cài đặt Stop Loss"}>
              <div className="lbl">{t("SLViolations")}</div>
              <div className="val r">{dashboardSummary.slViolations}</div>
              <div className="sub-val">{t("NoStopLoss")}</div>
            </div>

            <div className="tdv2-metric" data-tooltip={t("Teams_dashboard.Tooltip.missingJournal") || "Số lượng lệnh chưa được cập nhật nhật ký giao dịch"}>
              <div className="lbl">{t("NoJournal")}</div>
              <div className="val a">{dashboardSummary.missingJournal}</div>
              <div className="sub-val">{t("MissingJournal")}</div>
            </div>
          </div>

          <div className="tdv2-notif-rail-inline">
            <div className="tdv2-section-hd">
              <span>{t("ActionNeededNow")}</span>
              <button className="tdv2-view-all" onClick={() => setNotifOpen(true)}>
                {t("ViewAll")}{unreadCount > 0 ? ` (${unreadCount})` : ""}
              </button>
            </div>
            <div className="tdv2-notif-list">
              {topNotifications.map((n) => (
                <NotifItem key={n.id} n={n} />
              ))}
            </div>
          </div>

          <div className="tdv2-section-hd">{t("MemberPerformanceStage")}</div>
          <div className="tdv2-card">
            <div className="tdv2-card-title">{t("PerformanceTreemap")}</div>
            <div className="tdv2-card-sub">{t("TreemapHint")}</div>
            <Treemap data={charts?.treemap?.series.filter((s) => s.account_size !== 0) || []} />
            <div className="tdv2-tm-scale">
              <span>{t("BigLoss")}</span>
              <div className="tdv2-tm-scale-bar" />
              <span>{t("BigProfit")}</span>
            </div>
          </div>
          <div className="tdv2-charts-full">
            <div className="tdv2-card">
              <div className="tdv2-stage-trades">
                <div className="tdv2-stage-col">
                  <div className="tdv2-card-title">{t("StageDistribution")}</div>
                  <div className="tdv2-card-sub">{t("AccountStatus")}</div>
                  <div className="tdv2-donut-wrap" style={{ height: 140, marginBottom: 10 }}>
                    <Doughnut data={stageChartData} options={{ ...baseBarOpts, cutout: "68%" } as any} />
                    <div className="tdv2-donut-center">
                      <div className="big">{charts?.distribution.phases?.total || 0}</div>
                      <div className="sm">TK</div>
                    </div>
                  </div>
                  <div className="tdv2-stage-legend">
                    {stageDistribution.map((s) => (
                      <div className="chip" key={s.label} style={{ background: `${s.color}1a` }}>
                        <span className="dot" style={{ background: s.color }} />
                        <span className="n" style={{ color: s.color }}>{s.value || 0}</span>
                        <span>{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="tdv2-trades-col">
                  <div className="tdv2-card-title">{t("TradesByGroup")}</div>
                  <div className="tdv2-card-sub">{t("AccountStatus")}</div>
                  <div style={{ height: 200 }}>
                    <Bar
                      data={{
                        labels: ["Lãi", "Lỗ", "Hòa"],
                        datasets: [{ data: [pnlGroups["Lãi"], pnlGroups["Lỗ"], pnlGroups["Hòa"]], backgroundColor: ["#2D9B45", "#E24B4A", "#9B9589"], borderRadius: 4, borderSkipped: false }],
                      }}
                      options={{ ...baseBarOpts, scales: { y: { display: false }, x: { ticks: { font: { size: 13, weight: 600 } }, grid: { display: false } } } } as any}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="tdv2-card">
              <div className="tdv2-card-title">{t("PassChanceFTMO")}</div>
              <div className="tdv2-card-sub">{t("PassChanceHint")}</div>
              {/* Scroll horizontally when there are many traders so each bar stays readable. */}
              <div className="tdv2-bar-scroll">
                <div style={{ height: 240, minWidth: Math.max(passNames.length * 44, 320) }}>
                  <Bar
                    data={{ labels: passNames, datasets: [{ data: passScores, names: passChanceSorted.map(p => p.label), backgroundColor: passScores.map((v) => (v >= 70 ? "#2D9B45" : v >= 40 ? "#EF9F27" : "#E24B4A")), borderRadius: 4, borderSkipped: false } as any] }}
                    options={{ ...baseBarOpts, scales: { y: { min: 0, max: 100, ticks: { callback: (v: any) => v + "%", font: { size: 10 } }, grid: { color: "rgba(128,128,128,0.12)" } }, x: { ticks: { font: { size: 10 }, maxRotation: 60, minRotation: 45, autoSkip: false }, grid: { display: false } } } } as any}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="tdv2-charts-group">
            <div className="tdv2-card">
              <div className="tdv2-card-title">{t("MonthlyLosses")}</div>
              <div className="tdv2-card-sub">{t("MonthlyLossesHint")}</div>
              <div style={{ height: 200 }}>
                <Bar
                  data={losingTradesData}
                  options={{ ...baseBarOpts, scales: { y: { ticks: { stepSize: 2, font: { size: 10 } }, grid: { color: "rgba(128,128,128,0.12)" } }, x: { ticks: { font: { size: 10 } }, grid: { display: false } } } } as any}
                />
              </div>
            </div>
            <div className="tdv2-card">
              <div className="tdv2-card-title">{t("DisciplineViolations")}</div>
              <div className="tdv2-card-sub">{t("DisciplineHint")}</div>
              <div style={{ height: 200 }}>
                <Bar
                  data={violationsData}
                  options={{ ...baseBarOpts, plugins: { ...baseBarOpts.plugins, legend: { display: true, position: "top", labels: { font: { size: 10 }, boxWidth: 10 } } }, scales: { x: { ticks: { font: { size: 11 } }, grid: { display: false } }, y: { ticks: { stepSize: 1, font: { size: 10 } }, grid: { color: "rgba(128,128,128,0.12)" } } } } as any}
                />
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="tdv2-main-right">
          <div className="tdv2-section-hd">
            <span>{t("ActionNeededNow")}</span>
            <button className="tdv2-view-all" onClick={() => setNotifOpen(true)}>
              {t("ViewAll")}{unreadCount > 0 ? ` (${unreadCount})` : ""}
            </button>
          </div>
          <div className="tdv2-notif-list">
            {topNotifications.length === 0 ? (
              <div className="bg-gray-50/80 rounded-xl border border-dashed border-gray-300 p-8 flex flex-col items-center justify-center text-center h-[300px]">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 shadow-sm border border-gray-100">
                  <CheckSquareOffset size={32} className="text-green-500" />
                </div>

                <h4 className="!text-sm !font-bold !text-gray-800 mb-1.5">
                  {t("Teams_dashboard.team_topNotifications_noData_title")}
                </h4>

                <p className="!text-xs !text-gray-500 !leading-relaxed">
                  {t("Teams_dashboard.team_topNotifications_noData_desc")}
                </p>
              </div>
            ) : (
              <>
                {topNotifications.map((n) => (
                  <NotifItem key={n.id} n={n} />
                ))}
              </>
            )}
          </div>
        </div>

      </div>{/* end .tdv2-main-grid */}

      {/* MEMBER TABLE: full width */}
      <div className="tdv2-section-hd">{t("MemberDetails")}</div>
      <div className="tdv2-table-card">
        <div className="tdv2-table-head">
          <div>
            <div className="t">{t("MemberTracker")}</div>
            <div className="s">{t("UpdatedLastSession")}</div>
          </div>

          <div className="filterbar">
            <div className="filter-left">
              <div className={`status-wrap${statusOpen ? " open" : ""}`} onClick={(e) => e.stopPropagation()}>
                <button className="status-btn" onClick={() => { setStatusOpen(!statusOpen); setRangeOpen(false); }}>
                  <span className="dot-status" />
                  <span>
                    {filter === "all" ? t("Teams_dashboard.filter.AllStatuses") :
                      filter === "funded" ? t("Teams_dashboard.filter.Funded") :
                        filter === "challenge" ? t("Teams_dashboard.filter.Challenge") :
                          filter === "verification" ? t("Teams_dashboard.filter.Verification") :
                            filter === "danger" ? t("Danger") : t("Teams_dashboard.filter.All")}
                  </span>
                  <svg className="chev" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
                </button>
                <div className="status-menu">
                  {(["all", "funded", "challenge", "verification", "danger"] as TableFilter[]).map((f) => (
                    <button
                      key={f}
                      className={filter === f ? "sel" : f === "danger" ? "danger-opt" : ""}
                      onClick={() => { setFilter(f); setStatusOpen(false); }}
                    >
                      <span className="dot-status" />
                      {f === "all" ? t("Teams_dashboard.filter.All") : f === "danger" ? `⚠ ${t("Danger")}` : f === "funded" ? t("Teams_dashboard.filter.Funded") : f === "challenge" ? t("Teams_dashboard.filter.Challenge") : t("Teams_dashboard.filter.Verification")}
                    </button>
                  ))}
                </div>
              </div>

              <div className="divider" />

              {/* <div className="segmented">
                {(["all", "today", "week", "month"] as const).map((p) => (
                  <button
                    key={p}
                    className={period === p ? "active" : ""}
                    onClick={() => {
                      setPeriod(p);
                      setDateRange({ from: "", to: "" });
                      if (p === "all") {
                        setTableStartDate("all");
                        setTableEndDate("");
                      } else {
                        const now = new Date();
                        const to = now.toISOString().split('T')[0];
                        let from = to;
                        if (p === 'week') {
                          // Get Sunday of the current week
                          const day = now.getDay(); // 0 is Sunday, 1 is Monday...
                          const diff = now.getDate() - day;
                          const sun = new Date(now.setDate(diff));
                          from = sun.toISOString().split('T')[0];
                        } else if (p === 'month') {
                          from = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
                        }
                        setTableStartDate(from);
                        setTableEndDate(to);
                      }
                    }}
                  >
                    {p === "today" ? t("Teams_dashboard.filter.Today") :
                      p === "week" ? t("Teams_dashboard.filter.ThisWeek") :
                        p === "month" ? t("Teams_dashboard.filter.ThisMonth") : t("Teams_dashboard.filter.All")}
                  </button>
                ))}
              </div> */}

              <div className={`range-wrap${rangeOpen ? " open" : ""}`} onClick={(e) => e.stopPropagation()}>
                <button className="range-btn" title={t("Teams_dashboard.filter.CustomDateRange")} onClick={() => { setRangeOpen(!rangeOpen); setStatusOpen(false); }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                  <span className="rbtxt">
                    {dateRange.from && dateRange.to ? `${dateRange.from} → ${dateRange.to}` : t("Teams_dashboard.filter.SetDate")}
                  </span>
                </button>

                <div className="range-popover">
                  <h4>{t("Teams_dashboard.filter.SelectTimeRange")}</h4>
                  <div className="quick-ranges">
                    <button onClick={() => {
                      const d = new Date().toISOString().split('T')[0];
                      setDateRange({ from: d, to: d });
                    }}>{t("Teams_dashboard.filter.Today")}</button>
                    <button onClick={() => {
                      const d = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                      setDateRange({ from: d, to: d });
                    }}>{t("Teams_dashboard.filter.Yesterday")}</button>
                    {[7, 14, 30].map(d => (
                      <button key={d} onClick={() => {
                        const to = new Date().toISOString().split('T')[0];
                        const from = new Date(Date.now() - d * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
                        setDateRange({ from, to });
                      }}>
                        {d} {t("Teams_dashboard.filter.DaysAgo")}
                      </button>
                    ))}
                    <button
                      onClick={() => {
                        setDateRange({ from: "", to: "" });
                        setPeriod("all");
                        setTableStartDate("all");
                        setTableEndDate("");
                      }}>
                      {t("Teams_dashboard.filter.All")}
                    </button>
                  </div>
                  <div className="range-fields">
                    <div className="field">
                      <label>{t("Teams_dashboard.filter.FromDate")}</label>
                      <input type="date" value={dateRange.from} onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })} />
                    </div>
                    <span className="arrow-sep">→</span>
                    <div className="field">
                      <label>{t("Teams_dashboard.filter.ToDate")}</label>
                      <input type="date" value={dateRange.to} onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })} />
                    </div>
                  </div>
                  <div className="popover-actions">
                    <button className="btn-ghost" onClick={() => setRangeOpen(false)}>{t("Teams_dashboard.filter.Cancel")}</button>
                    <button className="btn-primary" onClick={() => {
                      if (dateRange.from && dateRange.to) {
                        setPeriod("custom");
                        setTableStartDate(dateRange.from);
                        setTableEndDate(dateRange.to);
                      }
                      setRangeOpen(false);
                    }}>{t("Teams_dashboard.filter.Apply")}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="tdv2-tbl-wrap">
          <table className="tdv2-table">
            <thead>
              <tr>
                <th>{t("Member")}</th>
                <th>{t("Stage")}</th>
                {/* <th>{t("Teams_dashboard.Pnl")}</th> */}
                <th>{t("Teams_dashboard.Drawdown")}</th>
                <th>{t("Teams_dashboard.Roadmap")}</th>
                <th>{t("NoStopLoss")}</th>
                <th>{t("Teams_dashboard.Journal")}</th>
                <th>{t("AccountHealth")}</th>
                <th>{t("Warnings")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((m) => (
                <MemberRow key={m.id} m={m} t={t} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* NOTIFICATIONS MODAL */}
      <Modal title={t("AllNotifications")} open={notifOpen} onCancel={() => setNotifOpen(false)} footer={null} width={620} wrapClassName="tdv2-modal-wrap">
        <div className="tdv2-modal">
          <div className="tdv2-modal-tabs">
            <button className={`tdv2-modal-tab${modalTab === "unread" ? " active" : ""}`} onClick={() => setModalTab("unread")}>
              {t("Unread")} ({notifications.filter((n) => !n.read).length})
            </button>
            <button className={`tdv2-modal-tab${modalTab === "read" ? " active" : ""}`} onClick={() => setModalTab("read")}>
              {t("Read")} ({notifications.filter((n) => n.read).length})
            </button>
          </div>
          <div className="tdv2-modal-list">
            {modalNotifications.length === 0 ? (
              <div className="tdv2-modal-empty">{t("NoNotifications")}</div>
            ) : (
              modalNotifications.map((n) => <NotifItem key={n.id} n={n} />)
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

const MemberRow: React.FC<{ m: DashboardMember; t: (k: string) => string }> = ({ m, t }) => {
  const navigate = useNavigate()
  const isDanger = m.dd >= 7 || m.sl >= 5;
  const stage = STAGE_MAP[m.stage];

  // health
  let health: { color: string; label: string };
  if (m.healthStatus) {
    const h = m.healthStatus.toLowerCase();
    if (h.includes("nguy hiểm") || h.includes("danger") || h.includes("red")) health = { color: "#E24B4A", label: m.healthStatus };
    else if (h.includes("cần chú ý") || h.includes("attention") || h.includes("amber") || h.includes("warning")) health = { color: "#EF9F27", label: m.healthStatus };
    else if (h.includes("phát triển") || h.includes("growing") || h.includes("blue")) health = { color: "#22C55E", label: m.healthStatus };
    else health = { color: "#4CAF50", label: m.healthStatus };
  } else {
    if (m.dd >= 7) health = { color: "#E24B4A", label: t("DangerHealth") };
    else if (m.dd >= 4 || m.sl >= 3) health = { color: "#EF9F27", label: t("NeedAttention") };
    else if (m.pnl >= 5) health = { color: "#22C55E", label: t("GrowingHealth") };
    else health = { color: "#4CAF50", label: t("Stable") };
  }

  // dd bar
  const ddPct = Math.min((m.dd / 10) * 100, 100);
  const ddCol = m.dd >= 7 ? "#E24B4A" : m.dd >= 4 ? "#EF9F27" : "#  ";

  // flags
  const flags: { cls: string; label: string }[] = [];
  if (m.alerts && m.alerts.length > 0) {
    m.alerts.forEach((alert) => {
      let cls = "a";
      const al = alert.toLowerCase();
      if (al.includes("vi phạm") || al.includes("danger") || al.includes("nguy hiểm") || al.includes("stop loss")) cls = "r";
      else if (al.includes("pass") || al.includes("tốt")) cls = "g";
      else if (al.includes("giảm") || al.includes("vốn")) cls = "b";
      flags.push({ cls, label: alert });
    });
  } else {
    if (m.sl >= 5) flags.push({ cls: "r", label: t("SLViolated") });
    else if (m.sl >= 2) flags.push({ cls: "a", label: t("SLAttention") });
    if (m.journal >= 4) flags.push({ cls: "r", label: t("MissingJournal") });
    else if (m.journal >= 1) flags.push({ cls: "a", label: t("JournalShort") });
    if (m.dd >= 7) flags.push({ cls: "r", label: t("NearMaxLoss") });
    else if (m.dd >= 4) flags.push({ cls: "a", label: t("HighDrawdown") });
    if (m.pnl >= 7.5) flags.push({ cls: "b", label: t("ReduceRisk") });
    if (m.pnl >= 4 && m.stage === "verification") flags.push({ cls: "g", label: t("AboutToPass") });
  }

  const slColor = m.sl === 0 ? "var(--green)" : m.sl <= 2 ? "var(--amber)" : "var(--red)";

  return (
    <tr className={isDanger ? "danger" : ""}>
      <td onClick={() => {
        window.open(`/${m.platform}/account-live-metrix/${m.account_number}`, "_self")
      }}>
        <div className="tdv2-name-cell cursor-pointer">
          {
            m.avatar_url ? (
              <img src={m.avatar_url} alt={m.name} className="tdv2-avatar" />
            ) : (
              <div className="tdv2-avatar" style={{ background: m.avBg, color: m.avColor }}>{m.shortName.charAt(0)}</div>
            )

          }
          <div>
            <div className="tdv2-nm">{m.name}</div>
            <div className="tdv2-acc-size">{m.account_number}</div>
          </div>
        </div>
      </td>
      <td>
        <span className="tdv2-stage-pill" style={{ background: stage.bg, color: stage.c }}>
          {m.stage === 'funded' ? t("Teams_dashboard.filter.Funded") :
            m.stage === 'challenge' ? t("Teams_dashboard.filter.Challenge") :
              m.stage === 'verification' ? t("Teams_dashboard.filter.Verification") :
                m.stage === 'new' ? t("New") : stage.label}
        </span>
      </td>
      {/* <td>
        <span className={`tdv2-pnl ${m.pnl >= 0 ? "pos" : "neg"}`}>{m.pnl >= 0 ? "+" : ""}{m.pnl}%</span>
      </td> */}
      <td>
        <div className="tdv2-dd-cell">
          <div className="tdv2-dd-bar"><div className="tdv2-dd-fill" style={{ width: `${ddPct}%`, background: ddCol }} /></div>
          <span style={{ fontSize: 12, color: ddCol }}>{m.dd}%</span>
        </div>
      </td>
      <td>
        {m.road > m.roadTarget ? (
          <div className="tdv2-road-cell">
            <div style={{ fontSize: 11, color: "var(--red)", fontWeight: 600 }}>{t("OutOfRoadmap")}</div>
          </div>
        ) : (
          <div className="tdv2-road-cell">
            <div className="tdv2-road-bar">
              <div
                className="tdv2-road-fill"
                style={{
                  width: `${Math.min((Math.max(0, m.road) / m.roadTarget) * 100, 100)}%`,
                  // Giữ hue xanh lá cố định (130), chỉ đậm dần theo % tiến độ
                  // 0%  -> HSL(130, 35%, 68%) xanh lá nhạt
                  // 100% -> HSL(130, 55%, 28%) xanh lá đậm
                  backgroundColor: (() => {
                    const pct = Math.min((Math.max(0, m.road) / m.roadTarget) * 100, 100);
                    const sat = 35 + pct * 0.20;   // 35% -> 55%
                    const light = 68 - pct * 0.40; // 68% -> 28%
                    return `hsl(130, ${sat}%, ${light}%)`;
                  })()
                }}
              />
            </div>
            <div className="tdv2-road-txt">{m.road >= 0 ? "+" : ""}{m.road}% → {m.roadTarget}%</div>
          </div>
        )}
      </td>
      <td>
        <span className="tdv2-sl" style={{ color: slColor }}>{m.sl} {t("Times")}</span>
      </td>
      <td>
        {m.journal === 0 ? (
          <span className="tdv2-sl" style={{ color: "var(--green)" }}>✓ {t("Enough")}</span>
        ) : (
          <span className="tdv2-sl" style={{ color: "var(--amber)" }}>{t("Missing")} {m.journal}</span>
        )}
      </td>
      <td>
        <div className="tdv2-health"><div className="tdv2-hdot" style={{ background: health.color }} /><span style={{ color: health.color }}>{health.label}</span></div>
      </td>
      <FlagCell flags={flags} />
    </tr>
  );
};

function FlagCell({ flags }: { flags: Array<{ cls: string; label: string }> }) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!flags || flags.length === 0) {
    return (
      <td>
        <div className="tdv2-flags">
          <span style={{ fontSize: 11, color: "var(--text-3)" }}>—</span>
        </div>
      </td>
    );
  }

  return (
    <td>
      <div className="tdv2-flags">
        {isExpanded || flags.length === 1 ? (
          flags.map((f, i) => (
            <span key={i} className={`tdv2-flag ${f.cls}`} onClick={(e) => { e.stopPropagation(); setIsExpanded(false); }} style={{ cursor: 'pointer' }}>
              {f.label}
            </span>
          ))
        ) : (
          <>
            <span className={`tdv2-flag ${flags[0].cls}`}>{flags[0].label}</span>
            <span
              className="tdv2-flag flag-more"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(true)
              }}
              style={{ cursor: 'pointer' }}
            >
              +{flags.length - 1}
            </span>
          </>
        )}
      </div>
    </td>
  );
}

export default TeamDashboardTabV2;
